"use client";

import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Knopf } from "../../knopf";
import {
  SymDuplizieren,
  SymHoch,
  SymKopfzeile,
  SymLinks,
  SymMitte,
  SymMitteAusrichten,
  SymObenAusrichten,
  SymPapierkorb,
  SymRechtsAusrichten,
  SymRunter,
  SymSpalteDanach,
  SymSpalteDavor,
  SymTeilen,
  SymUntenAusrichten,
  SymVerbinden,
  SymZeileDanach,
  SymZeileDavor,
} from "./bearbeiten-symbole";

/** Zustand des Tabellenwerkzeugs; gleiche Form wie `HornTabellenZustand` aus `@horn/editor` (`useHornTabelle`). */
export interface EditorTabellenZustand {
  rechteck: { left: number; top: number; right: number; bottom: number } | null;
  ausrichtung: "left" | "center" | "right";
  senkrecht: "top" | "middle" | "bottom";
  kannVerbinden: boolean;
  kannTeilen: boolean;
  kannZeileDuplizieren: boolean;
  kannSpalteDuplizieren: boolean;
  kannZeileHoch: boolean;
  kannZeileRunter: boolean;
}

/** Befehle des Tabellenwerkzeugs; gleiche Form wie `HornTabellenBefehle`. */
export interface EditorTabellenBefehle {
  zeileDavor: () => void;
  zeileDanach: () => void;
  spalteDavor: () => void;
  spalteDanach: () => void;
  zeileLoeschen: () => void;
  spalteLoeschen: () => void;
  kopfzeile: () => void;
  zeileDuplizieren: () => void;
  spalteDuplizieren: () => void;
  zeileHoch: () => void;
  zeileRunter: () => void;
  ausrichten: (a: "left" | "center" | "right") => void;
  senkrechtAusrichten: (a: "top" | "middle" | "bottom") => void;
  verbinden: () => void;
  teilen: () => void;
}

// Karte wie die Preline-Hover-Karte (Popover-Fläche); `fixed` am Fenster, damit sie vom Rahmen (`overflow-hidden`) nicht beschnitten wird.
const KARTE = "fixed top-0 start-0 z-60 flex max-w-full flex-wrap items-center gap-x-0.5 gap-y-1 rounded-xl border border-popover-line bg-popover p-1 shadow-md";
const GRUPPE = "flex items-center gap-x-0.5";
const GRUPPENNAME = "ps-1.5 pe-0.5 text-xs text-muted-foreground-1";
const TRENNER = "mx-1 h-5 border-s border-layer-line";
const ABSTAND = 8;

function Werkzeug({
  label,
  symbol,
  onClick,
  aktiv,
  gesperrt,
}: {
  label: string;
  symbol: ReactNode;
  onClick: () => void;
  /** Umschalter mit Dauerzustand (Ausrichtung): `aria-pressed`. */
  aktiv?: boolean;
  gesperrt?: boolean;
}) {
  return (
    <Knopf
      nurSymbol
      groesse="xs"
      variante={aktiv ? "sekundaer" : "ghost"}
      label={label}
      titel={label}
      symbol={symbol}
      starr
      ariaPressed={aktiv === undefined ? undefined : aktiv}
      disabled={gesperrt}
      onClick={onClick}
    />
  );
}

/**
 * Tabellenwerkzeug (Paket E-W2, G-W4: „Tabellenwerkzeug … aus Standardklassen“, Abweichung AW-064 (1), (8)): erscheint, solange die Auswahl in einer Tabelle
 * steht, als schwebende Karte über der Tabelle (bei langer Tabelle am oberen Fensterrand). Gruppen Zeile, Spalte, Zelle mit den
 * Befehlen der Altfläche (Zeile/Spalte davor, danach, duplizieren, löschen; Zeile nach oben/unten; Kopfzeile; Ausrichtung waagerecht
 * und senkrecht; Zellen verbinden/teilen), jeder Knopf mit Namen (`aria-label`, `title`). Der Baustein prüft keine Rechte und ändert
 * nichts selbst. Einbau: neben `HornEditorContent` im Rahmen (Tab-Reihenfolge); die Mausklicks lassen den Fokus im Editor.
 */
export function EditorTabellenWerkzeug({
  werkzeug,
}: {
  werkzeug: { zustand: EditorTabellenZustand; befehle: EditorTabellenBefehle } | null;
}) {
  const karte = useRef<HTMLDivElement>(null);
  const rechteck = werkzeug?.zustand.rechteck ?? null;

  // Platzieren vor dem ersten Zeichnen und nach jeder Änderung: über der Tabelle, am Fensterrand geklemmt, ausgeblendet, solange
  // die Tabelle nicht im Fenster steht. Die Koordinaten setzt der Baustein zur Laufzeit am Element (wie `dropdown-menue.tsx`).
  useLayoutEffect(() => {
    const k = karte.current;
    if (!k || !rechteck) return;
    const b = k.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const sichtbar = rechteck.bottom > ABSTAND && rechteck.top < vh - ABSTAND;
    k.style.visibility = sichtbar ? "visible" : "hidden";
    const oben = Math.min(Math.max(ABSTAND, rechteck.top - b.height - ABSTAND), vh - b.height - ABSTAND);
    const links = Math.max(ABSTAND, Math.min(rechteck.left, vw - b.width - ABSTAND));
    k.style.left = `${Math.round(links)}px`;
    k.style.top = `${Math.round(oben)}px`;
  });

  if (!werkzeug || !rechteck) return null;
  const { zustand: z, befehle: b } = werkzeug;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-editor-and-toolbar-menus/floating-canvas-toolbar */}
      <div
        ref={karte}
        className={KARTE}
        role="toolbar"
        aria-label="Tabellenwerkzeug"
        // Mausklicks lassen den Fokus im Editor (die Auswahl in der Zelle bleibt für die Befehle erhalten).
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className={GRUPPE} role="group" aria-label="Zeile">
          <span className={GRUPPENNAME} aria-hidden="true">
            Zeile
          </span>
          <Werkzeug label="Zeile davor einfügen" symbol={<SymZeileDavor />} onClick={b.zeileDavor} />
          <Werkzeug label="Zeile danach einfügen" symbol={<SymZeileDanach />} onClick={b.zeileDanach} />
          <Werkzeug label="Zeile duplizieren" symbol={<SymDuplizieren />} onClick={b.zeileDuplizieren} gesperrt={!z.kannZeileDuplizieren} />
          <Werkzeug label="Zeile nach oben" symbol={<SymHoch />} onClick={b.zeileHoch} gesperrt={!z.kannZeileHoch} />
          <Werkzeug label="Zeile nach unten" symbol={<SymRunter />} onClick={b.zeileRunter} gesperrt={!z.kannZeileRunter} />
          <Werkzeug label="Zeile löschen" symbol={<SymPapierkorb />} onClick={b.zeileLoeschen} />
        </div>
        <div className={TRENNER} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE} role="group" aria-label="Spalte">
          <span className={GRUPPENNAME} aria-hidden="true">
            Spalte
          </span>
          <Werkzeug label="Spalte davor einfügen" symbol={<SymSpalteDavor />} onClick={b.spalteDavor} />
          <Werkzeug label="Spalte danach einfügen" symbol={<SymSpalteDanach />} onClick={b.spalteDanach} />
          <Werkzeug label="Spalte duplizieren" symbol={<SymDuplizieren />} onClick={b.spalteDuplizieren} gesperrt={!z.kannSpalteDuplizieren} />
          <Werkzeug label="Spalte löschen" symbol={<SymPapierkorb />} onClick={b.spalteLoeschen} />
        </div>
        <div className={TRENNER} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE} role="group" aria-label="Zelle">
          <span className={GRUPPENNAME} aria-hidden="true">
            Zelle
          </span>
          <Werkzeug label="Kopfzeile umschalten" symbol={<SymKopfzeile />} onClick={b.kopfzeile} />
          <Werkzeug label="Text links" symbol={<SymLinks />} aktiv={z.ausrichtung === "left"} onClick={() => b.ausrichten("left")} />
          <Werkzeug label="Text mittig" symbol={<SymMitte />} aktiv={z.ausrichtung === "center"} onClick={() => b.ausrichten("center")} />
          <Werkzeug label="Text rechts" symbol={<SymRechtsAusrichten />} aktiv={z.ausrichtung === "right"} onClick={() => b.ausrichten("right")} />
          <Werkzeug label="Senkrecht oben" symbol={<SymObenAusrichten />} aktiv={z.senkrecht === "top"} onClick={() => b.senkrechtAusrichten("top")} />
          <Werkzeug label="Senkrecht mittig" symbol={<SymMitteAusrichten />} aktiv={z.senkrecht === "middle"} onClick={() => b.senkrechtAusrichten("middle")} />
          <Werkzeug label="Senkrecht unten" symbol={<SymUntenAusrichten />} aktiv={z.senkrecht === "bottom"} onClick={() => b.senkrechtAusrichten("bottom")} />
          {z.kannVerbinden ? <Werkzeug label="Zellen verbinden" symbol={<SymVerbinden />} onClick={b.verbinden} /> : null}
          {z.kannTeilen ? <Werkzeug label="Zelle teilen" symbol={<SymTeilen />} onClick={b.teilen} /> : null}
        </div>
      </div>
    </>
  );
}
